import React, { useCallback, useMemo } from 'react'
import {
  FormControlLabel,
  Link as MuiLink,
  Switch,
  Typography,
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { merge } from 'lodash'

import { useSearchFilters } from '../../../providers/search'
import {
  formatLinkWithTicker,
  formatPercentage,
  getMonthDisplay,
} from '../../../utils/functions'
import Html from '../../Html/Html'
import BenchmarkScrollingTable from '../../Tables/BenchmarkScrollingTable'

const HtmlLink = styled('div')(({ theme }) => ({
  '& a': {
    textDecoration: 'none',
    color: '#D8B45A',
  },
}))

const TextLink = styled('button')(({ theme }) => ({
  background: 'none',
  color: '#D8B45A',
  textDecoration: 'underline',
  border: 'none',
  padding: 0,
  font: 'inherit',
  cursor: 'pointer',
  outline: 'inherit',
}))

export default function AllFundPeriodicPerformance({
  data = [],
  period_code,
  fundGrouping,
  activeShareClass,
  presentation,
  html_content_before = [],
  showMarketValues = false,
  showMarketValuesToggle,
}) {
  const theme = useTheme()
  const mobileView = useMediaQuery(theme.breakpoints.down('sm'))

  const { filters, applyFilter } = useSearchFilters()

  const queryData = useMemo(() => data.filter((fund) => fund !== null), [data])

  const linkSx = useMemo(() => ({ fontWeight: 500 }), [])

  const fundNameRenderer = useCallback(
    ({ row, value }) =>
      row.benchmark ? (
        <span className="benchmark-name">{value}</span>
      ) : (
        <MuiLink href={formatLinkWithTicker(row)} underline="none" sx={linkSx}>
          {value}
        </MuiLink>
      ),
    [linkSx]
  )

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: 'Fund Name',
        accessor: 'title',
        id: 'title',
        Cell: fundNameRenderer,
      },
    ]

    if (queryData.length > 0) {
      queryData[0].periodicPerformance.forEach((item) => {
        const header =
          (period_code === '1MO'
            ? `${getMonthDisplay(item.calendar.monthShortName)} `
            : '') + item.calendar.yearNumber

        baseColumns.push({
          Header: header,
          accessor: item.calendar.key,
          id: item.calendar.key,
          Cell: ({ value }) => (
            <span>
              {typeof value === 'number' ? formatPercentage(value) : '--'}
            </span>
          ),
        })
      })
    }

    return baseColumns
  }, [queryData, period_code, fundNameRenderer])

  const hasMarketPrice = useMemo(
    () =>
      queryData.some((fund) =>
        fund.periodicPerformance.some(
          (period) => period.marketReturn !== undefined
        )
      ),
    [queryData]
  )

  const fundData = useMemo(() => {
    return queryData.reduce((result, fund) => {
      const tickerMap = {}
      const fundValues = fund.periodicPerformance.reduce(
        (perfResult, period) => {
          perfResult[period.calendar.key] = showMarketValues
            ? period.marketReturn
            : period.fundReturn
          return perfResult
        },
        {}
      )

      let benchmarkList = []
      if (fund.periodicPerformance[0]?.indexReturns) {
        benchmarkList = fund.periodicPerformance[0].indexReturns.map(
          (bm, idx) => {
            const indexValues = {
              title: bm.benchmarkIndex.name,
              benchmark: true,
            }
            fund.periodicPerformance.forEach((period) => {
              indexValues[period.calendar.key] =
                period.indexReturns[idx]?.indexReturn
            })
            return indexValues
          }
        )
      }

      tickerMap.fund = fundValues
      tickerMap.benchmarks = benchmarkList
      result[fund.ticker] = tickerMap

      return result
    }, {})
  }, [queryData, showMarketValues])

  const findFundByShareClass = useCallback(
    (fund) => {
      return fund.fund_classes?.find(
        (shareClass) =>
          shareClass.share_class[0].title === activeShareClass.title
      )
    },
    [activeShareClass]
  )

  const tables = useMemo(() => {
    return fundGrouping.group.map((group) => {
      const tableData = []
      const benchmarkData = []
      let shaded = false

      tableData.push({ title: group.title, innerTitle: group.title })
      benchmarkData.push({ title: group.title, innerTitle: group.title })

      group.products.forEach((fund) => {
        const fundByShareClassObj = findFundByShareClass(fund)
        if (fundByShareClassObj) {
          const perfvalues = fundData[fundByShareClassObj.ticker]
          const mergedFundObject = merge(
            {},
            fund,
            fundByShareClassObj,
            perfvalues?.fund
          )

          tableData.push({ ...mergedFundObject, rowOptions: { shaded } })
          benchmarkData.push({ ...mergedFundObject, rowOptions: { shaded } })

          if (perfvalues?.benchmarks) {
            perfvalues.benchmarks.forEach((benchmarkObj) => {
              benchmarkData.push({ ...benchmarkObj, rowOptions: { shaded } })
            })
          }
        }
        shaded = !shaded
      })

      return {
        title: group.title,
        data: tableData,
        benchmarkData,
      }
    })
  }, [fundGrouping, findFundByShareClass, fundData])

  const filteredTables = useMemo(() => {
    const filtered = tables
      .map((table) => (applyFilter ? applyFilter(table, filters) : table))
      .filter((response) => response)
    return filtered
  }, [tables, applyFilter, filters])

  const combinedTable = useMemo(() => {
    return filteredTables.reduce(
      (acc, table) => {
        acc.data.push(...table.data)
        acc.benchmarkData.push(...table.benchmarkData)
        return acc
      },
      { data: [], benchmarkData: [] }
    )
  }, [filteredTables])

  const formControlLabelSx = useMemo(() => ({ marginLeft: 0 }), [])
  const presentationObj = useMemo(() => ({ presentation }), [presentation])

  const handleClearFilter = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }, [])

  const handleShowMarketValuesToggle = useCallback(() => {
    if (showMarketValuesToggle) {
      showMarketValuesToggle()
    }
  }, [showMarketValuesToggle])

  const htmlBlocks = useMemo(
    () =>
      html_content_before.map((content_block, idx) => (
        <Html key={idx} content={content_block.html} className={HtmlLink} />
      )),
    [html_content_before]
  )

  const scrollingTableProps = useMemo(
    () => ({
      columns,
      data: filteredTables,
      benchmarkData: combinedTable.benchmarkData,
      presentation: presentationObj,
      className: 'table-headers--centered',
    }),
    [columns, filteredTables, combinedTable.benchmarkData, presentationObj]
  )
  const switchControl = useCallback(<Switch checked={showMarketValues} />)
  return (
    <>
      <Typography className="price-title">
        {period_code === '1MO' ? 'As of Month-Ended' : 'As of Year-Ended 12/31'}
      </Typography>

      {htmlBlocks}

      {mobileView && hasMarketPrice && (
        <FormControlLabel
          sx={formControlLabelSx}
          control={switchControl}
          label="NAV/Market Price"
          labelPlacement="start"
          onChange={handleShowMarketValuesToggle}
        />
      )}

      {combinedTable.data.length === 0 ? (
        <Typography variant="body2">
          Your criteria produced no results.{' '}
          <TextLink onClick={handleClearFilter}>Clear filter</TextLink> to
          explore our products.
        </Typography>
      ) : (
        <BenchmarkScrollingTable {...scrollingTableProps} />
      )}
    </>
  )
}